import React, { useState } from "react";
import { makeStyles } from "@mui/styles";
import ModalContainer from "components/ModalContainer";
import LoadingIndicator from "components/LoadingIndicator";

const useStyles = makeStyles((theme) => ({
  imagePreviewContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "20px",
    maxWidth: "90vw",
    maxHeight: "90vh",
  },
  previewImage: {
    maxWidth: "100%",
    maxHeight: "70vh",
    objectFit: "contain",
    borderRadius: "8px",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
  },
  imageTitle: {
    marginBottom: "16px",
    fontSize: "18px",
    fontWeight: "bold",
    textAlign: "center",
    color: theme.palette.text.primary,
  },
  noImageText: {
    color: theme.palette.grey[600],
    fontSize: "14px",
    fontStyle: "italic",
  },
  imageLoader: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "200px",
    flexDirection: "column",
    gap: "16px",
  },
  loadingText: {
    color: theme.palette.grey[600],
    fontSize: "14px",
  },
}));

interface ImagePreviewModalProps {
  open: boolean;
  onClose: () => void;
  imageUrl?: string;
  title?: string;
  alt?: string;
}

const ImagePreviewModal: React.FC<ImagePreviewModalProps> = ({
  open,
  onClose,
  imageUrl,
  title = "Image Preview",
  alt,
}) => {
  const styles = useStyles();
  const [imageLoading, setImageLoading] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Reset states when modal opens
  React.useEffect(() => {
    if (open && imageUrl) {
      setImageLoading(true);
      setImageError(false);
    } else if (!open) {
      setImageLoading(false);
      setImageError(false);
    }
  }, [open, imageUrl]);

  const handleImageLoad = () => {
    setImageLoading(false);
    setImageError(false);
  };

  const handleImageError = () => {
    setImageLoading(false);
    setImageError(true);
  };

  const handleClose = () => {
    setImageLoading(false);
    setImageError(false);
    onClose();
  };

  return (
    <ModalContainer openModel={open} handleClose={handleClose}>
      <div className={styles.imagePreviewContainer}>
        {title && <div className={styles.imageTitle}>{title}</div>}

        {imageLoading && (
          <div className={styles.imageLoader}>
            <LoadingIndicator />
            <div className={styles.loadingText}>Loading image...</div>
          </div>
        )}

        {imageUrl ? (
          <>
            <img
              src={imageUrl}
              alt={alt || title || "Preview"}
              className={styles.previewImage}
              style={{
                display: imageLoading || imageError ? "none" : "block",
              }}
              onLoad={handleImageLoad}
              onError={handleImageError}
            />
            {imageError && (
              <div className={styles.noImageText}>Failed to load image</div>
            )}
          </>
        ) : (
          <div className={styles.noImageText}>No image available</div>
        )}
      </div>
    </ModalContainer>
  );
};

export default ImagePreviewModal;
